/**
 * The device-data-collection frame.
 *
 * A hidden iframe pointed at the URL Payer Auth Setup returned, with the access
 * token posted in as a field named `JWT`. The frame posts back when it has
 * collected what it needs; the message is believed only under the same
 * three-part rule the 3-D Secure challenge uses, against `ddcOrigin` — which the
 * **server** derived from the returned URL rather than this code hardcoding a
 * Cardinal/3DS host (06 §3.8).
 *
 * ## Why the field is named `JWT` and not `access_token`
 *
 * Cardinal's collector reads a form field literally called `JWT`. The working
 * reference posts `{ name: 'JWT', value: accessToken }`; the first version of
 * this file posted `access_token`, which the collector silently ignores. The
 * frame loads, collects nothing, and says nothing — device data is simply
 * always empty, which quietly weakens the fraud signal on every payment.
 *
 * ## What the frame actually posts back
 *
 * Not a message of our own invention. Cardinal sends `{ MessageType:
 * 'profile.completed', … }`, either as an object or as a JSON **string**, so
 * both are handled. Our own `ddc-complete` is also accepted, because that is
 * what the API's `3ds-return` route sends for the other half of this flow.
 */
import { useEffect, useRef } from 'react';
import { isTrustedDdcEvent, parseDdcMessage } from './messages';

export interface DdcFrameProps {
  deviceDataCollectionUrl: string;
  accessToken: string;
  /** Derived server-side from `deviceDataCollectionUrl`. */
  ddcOrigin: string;
  onCollected: () => void;
  onFailed: () => void;
}

/**
 * The hand-off between the frame and the flow.
 *
 * The flow must not enroll before the collector has had its chance, but it must
 * not wait on it either, so the rule lives in one object with one timeout
 * (supplied by the caller, because the flow owns how long it can wait).
 */
export interface DeviceDataGate {
  /** Called by the frame once it reports collection. */
  collected(): void;
  /** Resolve on collection, or after `timeoutMs` — never reject. */
  wait(timeoutMs: number): Promise<void>;
}

/**
 * The hand-off between the frame and the flow.
 *
 * The flow must not enroll before the collector has had its chance, but it must
 * not wait on it either, so the rule lives in one object with one timeout.
 */
export function createDeviceDataGate(): DeviceDataGate {
  let settle: () => void = () => undefined;
  let done = false;
  const collected = (): void => {
    if (done) return;
    done = true;
    settle();
  };
  return {
    collected,
    wait(timeoutMs: number): Promise<void> {
      if (done) return Promise.resolve();
      return new Promise<void>((resolve) => {
        settle = () => {
          clearTimeout(timer);
          resolve();
        };
        const timer = setTimeout(() => {
          done = true;
          resolve();
        }, timeoutMs);
      });
    },
  };
}

export default function DdcFrame({
  deviceDataCollectionUrl,
  accessToken,
  ddcOrigin,
  onCollected,
  onFailed,
}: DdcFrameProps) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const handlers = useRef({ onCollected, onFailed });
  handlers.current = { onCollected, onFailed };

  useEffect(() => {
    const onMessageEvent = (event: MessageEvent): void => {
      if (!isTrustedDdcEvent(event, { origin: ddcOrigin, frame: frameRef.current })) return;
      const message = parseDdcMessage(event.data);
      if (message === 'complete') handlers.current.onCollected();
      else if (message === 'failed') handlers.current.onFailed();
    };
    window.addEventListener('message', onMessageEvent);
    return () => window.removeEventListener('message', onMessageEvent);
  }, [ddcOrigin]);

  return (
    <iframe
      ref={frameRef}
      title="device-data-collection"
      name="ddc-frame"
      // Hidden in `styles.css` (`.ddc-frame`), not with an inline style: this
      // frame collects a fingerprint and is never something a donor interacts
      // with, but it still has to be in the document for the collector to run.
      className="ddc-frame"
      data-testid="ddc-frame"
    >
      <form
        method="POST"
        action={deviceDataCollectionUrl}
        target="ddc-frame"
        data-testid="ddc-form"
      >
        {/* Named `JWT`: the collector reads that field and nothing else. */}
        <input type="hidden" name="JWT" value={accessToken} readOnly />
      </form>
    </iframe>
  );
}
